import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useCallback, useContext, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import OtherContactService from "services/OtherContact.service";
import {
  validateEmail,
  validatePhoneNumber,
} from "shared/utils/validator.utils";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  contentContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 15,
    width: "100%",
    minWidth: 600,
  },
  rowContainer: {
    flexDirection: "row",
    gap: 15,
    width: "100%",
  },
  flexOne: {
    flex: 1,
  },
  threeColumnContainer: {
    flexDirection: "row",
    gap: 20,
    width: "100%",
  },
  columnSection: {
    flex: 1,
    minWidth: 180,
  },
  associationSection: {
    marginTop: 15,
    padding: 15,
    backgroundColor: theme.color.lightGrey,
    borderRadius: 8,
  },
  associationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  associationTitle: {
    fontSize: 14,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.backgroundText,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 4,
    backgroundColor: theme.color.background,
    borderRadius: 4,
    marginVertical: 2,
  },
  contactInfo: {
    flex: 1,
  },
  contactName: {
    fontSize: 14,
    color: theme.color.backgroundText,
  },
  contactJobTitle: {
    fontSize: 12,
    color: theme.color.darkGrey,
    fontStyle: "italic",
  },
  errorText: {
    color: theme.color.red,
    fontSize: 12,
    marginTop: 4,
  },
});

export default function CreateOtherContactModal({
  visible,
  onClose,
  onContactCreated,
}) {
  const {
    locationList,
    organizationSystemMap,
    otherContactList,
    organizationColor,
    getOtherContacts,
  } = useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    organization: "",
    phone: "",
    email: "",
    secondaryEmail: "",
    locationIds: [],
    systemIds: [],
    backupContactId: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (formData.secondaryEmail && !validateEmail(formData.secondaryEmail)) {
      newErrors.secondaryEmail = "Invalid email address";
    }

    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const toggleLocation = (locationId) => {
    setFormData((prev) => ({
      ...prev,
      locationIds: prev.locationIds.includes(locationId)
        ? prev.locationIds.filter((id) => id !== locationId)
        : [...prev.locationIds, locationId],
    }));
  };

  const toggleSystem = (systemId) => {
    setFormData((prev) => ({
      ...prev,
      systemIds: prev.systemIds.includes(systemId)
        ? prev.systemIds.filter((id) => id !== systemId)
        : [...prev.systemIds, systemId],
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const contactData = {
        name: formData.name,
        description: formData.description || null,
        organization: formData.organization || null,
        phone: formData.phone || null,
        email: formData.email || null,
        secondaryEmail: formData.secondaryEmail || null,
        backupContactId: formData.backupContactId || null,
      };

      const response =
        await OtherContactService.createOtherContact(contactData);
      const newContactId = response.payload.otherContactId;

      if (formData.locationIds.length > 0) {
        await OtherContactService.updateOtherContactLocations(
          newContactId,
          formData.locationIds,
        );
      }

      if (formData.systemIds.length > 0) {
        await OtherContactService.updateOtherContactSystems(
          newContactId,
          formData.systemIds,
        );
      }

      // Reset form
      setFormData({
        name: "",
        description: "",
        organization: "",
        phone: "",
        email: "",
        secondaryEmail: "",
        locationIds: [],
        systemIds: [],
        backupContactId: "",
      });
      setErrors({});

      // Refresh the contacts list and close modal
      await getOtherContacts();
      onContactCreated();
      showToast("Contact created successfully", "success");
    } catch (error) {
      console.error("Error creating contact:", error);
      showToast("Failed to create contact", "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onContactCreated, showToast, getOtherContacts, validateForm]);

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      organization: "",
      phone: "",
      email: "",
      secondaryEmail: "",
      locationIds: [],
      systemIds: [],
      backupContactId: "",
    });
    setErrors({});
    onClose();
  };

  const systemOptions = Array.from(organizationSystemMap.values());
  const backupContactOptions = otherContactList || [];

  return (
    <CustomModal
      open={visible}
      title="Create New Contact"
      onClose={handleClose}
      canSave={!isSubmitting && formData.name.trim().length > 0}
      saveTitle={isSubmitting ? "Creating..." : "Create Contact"}
      onSave={handleSubmit}
      scroll
    >
      <View style={classes.contentContainer}>
        <LabelInput
          label="Name"
          placeholder="Contact name"
          value={formData.name}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, name: text }))
          }
          required
        />
        {errors.name && <Text style={classes.errorText}>{errors.name}</Text>}

        <LabelInput
          label="Description"
          placeholder="Description"
          value={formData.description}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, description: text }))
          }
        />

        <LabelInput
          label="Organization"
          placeholder="Organization"
          value={formData.organization}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, organization: text }))
          }
        />

        <View style={classes.rowContainer}>
          <View style={classes.flexOne}>
            <LabelInput
              label="Phone"
              placeholder="Phone number"
              value={formData.phone}
              onChangeText={(text) =>
                setFormData((prev) => ({ ...prev, phone: text }))
              }
            />
            {errors.phone && (
              <Text style={classes.errorText}>{errors.phone}</Text>
            )}
          </View>
          <View style={classes.flexOne}>
            <LabelInput
              label="Email"
              placeholder="Email address"
              value={formData.email}
              onChangeText={(text) =>
                setFormData((prev) => ({ ...prev, email: text }))
              }
            />
            {errors.email && (
              <Text style={classes.errorText}>{errors.email}</Text>
            )}
          </View>
        </View>

        <LabelInput
          label="Secondary Email"
          placeholder="Secondary email address"
          value={formData.secondaryEmail}
          onChangeText={(text) =>
            setFormData((prev) => ({ ...prev, secondaryEmail: text }))
          }
        />
        {errors.secondaryEmail && (
          <Text style={classes.errorText}>{errors.secondaryEmail}</Text>
        )}

        <View style={classes.threeColumnContainer}>
          {locationList.length > 0 && (
            <View style={classes.columnSection}>
              <View style={classes.associationSection}>
                <View style={classes.associationHeader}>
                  <Text style={classes.associationTitle}>Locations</Text>
                </View>
                {locationList.map((location) => (
                  <PressableContainer
                    key={location.locationId}
                    style={classes.contactItem}
                    onPress={() => toggleLocation(location.locationId)}
                  >
                    <View style={classes.contactInfo}>
                      <Text style={classes.contactName}>{location.name}</Text>
                    </View>
                    <Icon
                      name={
                        formData.locationIds.includes(location.locationId)
                          ? "check"
                          : "checkbox-blank-outline"
                      }
                      size={25}
                      color={organizationColor}
                    />
                  </PressableContainer>
                ))}
              </View>
            </View>
          )}

          {systemOptions.length > 0 && (
            <View style={classes.columnSection}>
              <View style={classes.associationSection}>
                <View style={classes.associationHeader}>
                  <Text style={classes.associationTitle}>Systems</Text>
                </View>
                {systemOptions.map((system) => (
                  <PressableContainer
                    key={system.organizationSystemId}
                    style={classes.contactItem}
                    onPress={() => toggleSystem(system.organizationSystemId)}
                  >
                    <View style={classes.contactInfo}>
                      <Text style={classes.contactName}>{system.title}</Text>
                    </View>
                    <Icon
                      name={
                        formData.systemIds.includes(system.organizationSystemId)
                          ? "check"
                          : "checkbox-blank-outline"
                      }
                      size={25}
                      color={organizationColor}
                    />
                  </PressableContainer>
                ))}
              </View>
            </View>
          )}

          {backupContactOptions.length > 0 && (
            <View style={classes.columnSection}>
              <View style={classes.associationSection}>
                <View style={classes.associationHeader}>
                  <Text style={classes.associationTitle}>Backup Contact</Text>
                </View>
                {backupContactOptions.map((contact) => (
                  <PressableContainer
                    key={contact.otherContactId}
                    style={classes.contactItem}
                    onPress={() =>
                      setFormData((prev) => ({
                        ...prev,
                        backupContactId:
                          prev.backupContactId === contact.otherContactId
                            ? ""
                            : contact.otherContactId,
                      }))
                    }
                  >
                    <View style={classes.contactInfo}>
                      <Text style={classes.contactName}>{contact.name}</Text>
                      {contact.organization && (
                        <Text style={classes.contactJobTitle}>
                          {contact.organization}
                        </Text>
                      )}
                    </View>
                    <Icon
                      name={
                        formData.backupContactId === contact.otherContactId
                          ? "check"
                          : "checkbox-blank-outline"
                      }
                      size={25}
                      color={organizationColor}
                    />
                  </PressableContainer>
                ))}
              </View>
            </View>
          )}
        </View>
      </View>
    </CustomModal>
  );
}
